import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  IReportDataSourcesQueryParams,
  IReportDataSourcesCountQueryParams,
  ICreateReportDataSource,
  IUpdateReportDataSource
} from "../../shared";
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class ReportDataSourcesService {
  private readonly baseUrl = environment.baseBackendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService
  ) { }

  /**
   * Obtiene todos los ReportDataSources con parámetros de consulta opcionales
   * @param params Parámetros de consulta OData
   */
  getReportDataSources(params?: IReportDataSourcesQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/ReportDataSources`, {
      params: this.buildQueryParams(params)
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea un nuevo ReportDataSource
   * @param reportDataSourceData Datos del nuevo ReportDataSource
   */
  createReportDataSource(reportDataSourceData: ICreateReportDataSource): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/odata/ReportDataSources`, reportDataSourceData).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene el conteo de ReportDataSources
   * @param params Parámetros de filtro opcionales
   */
  getReportDataSourcesCount(params?: IReportDataSourcesCountQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/ReportDataSources/$count`, {
      params: this.buildQueryParams(params)
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina un ReportDataSource por key (formato OData)
   * @param key Key del ReportDataSource a eliminar
   */
  deleteReportDataSourceByKey(key: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/odata/ReportDataSources(${key})`).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene un ReportDataSource por key (formato OData)
   * @param key Key del ReportDataSource
   * @param params Parámetros de consulta opcionales
   */
  getReportDataSourceByKey(key: number, params?: { $select?: string }): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/ReportDataSources(${key})`, {
      params: this.buildQueryParams(params)
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Actualiza un ReportDataSource por key (formato OData)
   * @param key Key del ReportDataSource a actualizar
   * @param reportDataSourceData Datos del ReportDataSource a actualizar
   */
  updateReportDataSourceByKey(key: number, reportDataSourceData: IUpdateReportDataSource): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/odata/ReportDataSources(${key})`, reportDataSourceData).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina un ReportDataSource por key (formato REST)
   * @param key Key del ReportDataSource a eliminar
   */
  deleteReportDataSource(key: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/odata/ReportDataSources/${key}`).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene un ReportDataSource por key (formato REST)
   * @param key Key del ReportDataSource
   * @param params Parámetros de consulta opcionales
   */
  getReportDataSource(key: number, params?: { $select?: string }): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/ReportDataSources/${key}`, {
      params: this.buildQueryParams(params)
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Actualiza un ReportDataSource por key (formato REST)
   * @param key Key del ReportDataSource a actualizar
   * @param reportDataSourceData Datos del ReportDataSource a actualizar
   */
  updateReportDataSource(key: number, reportDataSourceData: IUpdateReportDataSource): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/odata/ReportDataSources/${key}`, reportDataSourceData).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Construye los parámetros de consulta HTTP
   * @param params Parámetros de consulta
   * @returns HttpParams
   */
  private buildQueryParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }
    return httpParams;
  }
}
